import { apiClient } from './client'

// ============================================================================
// Types
// ============================================================================

export interface Inquiry {
  inquiryId: number
  category: string
  title: string
  status: 'pending' | 'answered' | 'closed'
  replyCount: number
  createdAt: string
}

export interface InquiryDetail {
  inquiryId: number
  category: string
  title: string
  content: string
  status: 'pending' | 'answered' | 'closed'
  createdAt: string
  attachments: InquiryAttachment[]
  replies: InquiryReply[]
}

export interface InquiryAttachment {
  attachmentId: number
  fileName: string
  fileSize: number
  downloadUrl: string | null
}

export interface InquiryReply {
  replyId: number
  authorName: string
  authorRole: string
  content: string
  createdAt: string
}

export interface InquiryListResponse {
  content: Inquiry[]
  totalElements: number
  totalPages: number
  page: number
  size: number
}

export interface InquiryCreateResponse {
  inquiryId: number
  category: string
  title: string
  status: string
  createdAt: string
}

// ============================================================================
// API Functions
// ============================================================================

export const inquiryApi = {
  /** 내 문의 목록 조회 */
  getList: async (params: { page: number; size: number }): Promise<InquiryListResponse> => {
    const response = await apiClient.get<{ success: boolean; data: InquiryListResponse }>(
      '/support/inquiries/me',
      { params }
    )
    return response.data.data
  },

  /** 문의 상세 조회 */
  getById: async (inquiryId: number): Promise<InquiryDetail> => {
    const response = await apiClient.get<{ success: boolean; data: InquiryDetail }>(
      `/support/inquiries/${inquiryId}`
    )
    return response.data.data
  },

  /** 추가 문의 등록 */
  createReply: async (inquiryId: number, content: string): Promise<{ replyId: number; content: string; inquiryStatus: string; createdAt: string }> => {
    const response = await apiClient.post<{ success: boolean; data: { replyId: number; content: string; inquiryStatus: string; createdAt: string } }>(
      `/support/inquiries/${inquiryId}/replies`,
      { content }
    )
    return response.data.data
  },

  /** 문의 등록 (multipart/form-data) */
  create: async (data: {
    category: string
    title: string
    content: string
    files?: File[]
  }): Promise<InquiryCreateResponse> => {
    const formData = new FormData()
    formData.append('category', data.category)
    formData.append('title', data.title)
    formData.append('content', data.content)
    if (data.files) {
      data.files.forEach((file) => {
        formData.append('files', file)
      })
    }
    const response = await apiClient.post<{ success: boolean; data: InquiryCreateResponse }>(
      '/support/inquiries',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data.data
  },
}
